import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, CalendarDays, Tag } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SectionHeading, Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (value) => {
  if (!value) return "";
  return new Intl.DateTimeFormat("es-ES", { dateStyle: "long" }).format(new Date(value));
};

const ArticleMeta = ({ article }) => (
  <div className="flex flex-wrap items-center gap-4 text-xs text-[#7D86C2]">
    <span className="inline-flex items-center gap-1.5"><CalendarDays size={14} /> {formatDate(article.published_at)}</span>
    <span className="inline-flex items-center gap-1.5"><Tag size={14} /> {article.category}</span>
    <span>{article.author}</span>
  </div>
);

export default function NewsPage() {
  const { slug } = useParams();
  const [items, setItems] = useState([]);
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    const url = slug ? `${API}/news/${encodeURIComponent(slug)}` : `${API}/news`;
    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error("News request failed");
        return response.json();
      })
      .then((data) => {
        if (cancelled) return;
        if (slug) setArticle(data);
        else setItems(data.items || []);
      })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  if (slug) {
    return (
      <div className="bg-[#14163C] min-h-screen" data-testid="news-detail-page">
        <Seo
          title={article ? `${article.title} | Cibermedida` : "Noticias | Cibermedida"}
          description={article?.excerpt || "Noticias y análisis sobre inteligencia artificial aplicada y segura."}
        />
        <Navbar />
        <main className="pt-32 pb-24">
          <div className="max-w-4xl mx-auto px-5 sm:px-8">
            <Link to="/noticias" className="inline-flex items-center gap-2 text-sm text-[#3FE0F2] hover:text-[#7FEBF8] transition-colors">
              <ArrowLeft size={16} /> Volver a noticias
            </Link>
            {loading && <p className="mt-12 text-[#B9C3EC]">Cargando noticia…</p>}
            {error && <p className="mt-12 text-[#FF9A9A]">No se ha podido cargar esta noticia.</p>}
            {article && (
              <article className="mt-10">
                <ArticleMeta article={article} />
                <h1 className="font-heading mt-5 text-4xl sm:text-5xl font-bold tracking-tight text-[#EEFBFF] leading-tight">{article.title}</h1>
                <p className="mt-6 text-lg text-[#B9C3EC] leading-relaxed">{article.excerpt}</p>
                {article.image_url && <img src={article.image_url} alt="" width="1200" height="675" className="mt-10 w-full rounded-2xl object-cover max-h-[520px]" />}
                <div className="mt-10 whitespace-pre-line text-base text-[#D6DDF5] leading-8">{article.content}</div>
                {article.source_url && <a href={article.source_url} target="_blank" rel="noreferrer" className="mt-8 inline-flex items-center gap-2 text-sm text-[#3FE0F2] hover:text-[#7FEBF8]">Fuente original <ArrowUpRight size={15} /></a>}
              </article>
            )}
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-[#14163C] min-h-screen" data-testid="news-page">
      <Seo title="Noticias de Inteligencia Artificial | Cibermedida" description="Noticias, análisis y novedades sobre inteligencia artificial aplicada, segura y responsable." />
      <Navbar />
      <main className="pt-32 pb-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHeading kicker="Actualidad" title="Noticias de inteligencia artificial" description="Novedades, análisis y claves para aplicar la IA con seguridad y retorno real." testId="news-heading" />
          {loading && <p className="text-[#B9C3EC]">Cargando noticias…</p>}
          {error && <p className="text-[#FF9A9A]">No se han podido cargar las noticias.</p>}
          {!loading && !error && items.length === 0 && <p className="text-[#B9C3EC]">Todavía no hay noticias publicadas.</p>}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((item, index) => (
              <Reveal key={item.id || item.slug} delay={index * 0.05}>
                <article className="h-full bg-[#242764] border border-[#3FE0F2]/15 rounded-2xl overflow-hidden card-glow">
                  {item.image_url && <img src={item.image_url} alt="" width="800" height="450" loading="lazy" className="w-full h-48 object-cover" />}
                  <div className="p-6">
                    <ArticleMeta article={item} />
                    <h2 className="font-heading mt-4 text-xl font-semibold text-[#EEFBFF] leading-snug">{item.title}</h2>
                    <p className="mt-3 text-sm text-[#B9C3EC] leading-relaxed line-clamp-3">{item.excerpt}</p>
                    <Link to={`/noticias/${item.slug}`} className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#3FE0F2] hover:text-[#7FEBF8] transition-colors">Leer noticia <ArrowUpRight size={15} /></Link>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
